import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getVDriveStrategySuggestion,
  queryKeys,
  startVDriveStrategySuggestion,
  VDriveStrategySuggestionJob,
  VDriveStrategySuggestionScope,
} from '@vgrubs/components';
import { isAxiosError } from 'axios';
import { useState } from 'react';

/** How often a running job is asked about, as the backend asks. */
const POLL_MS = 5000;

const storageKey = (scope: VDriveStrategySuggestionScope, id: string) =>
  `vdrive-strategy-suggestion:${scope}:${id}`;

/*
 * The job id is kept for the tab, so a reload picks the job (or its finished
 * result) back up instead of paying for a new AI run. Storage can be missing
 * or throw (private windows, blocked site data); then a reload just forgets.
 */
const readStoredJob = (key: string) => {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
};

const storeJob = (key: string, jobId: string | null) => {
  try {
    if (jobId) sessionStorage.setItem(key, jobId);
    else sessionStorage.removeItem(key);
  } catch {
    // Remembering the job is a convenience only.
  }
};

const isNotFound = (error: unknown) =>
  isAxiosError(error) && error.response?.status === 404;

/** The backend's sentence for a refused start, or a generic one. */
const startErrorMessage = (error: unknown) => {
  const sent = isAxiosError(error) ? error.response?.data?.message : undefined;
  // class-validator answers a bad body with a list of sentences.
  const message = Array.isArray(sent) ? sent.join(' ') : sent;
  return message || 'The AI suggestion could not be started.';
};

export type StrategySuggestionStatus =
  | 'idle'
  | 'starting'
  | 'pending'
  | 'done'
  | 'failed';

type Params = {
  scope: VDriveStrategySuggestionScope;
  /** The location's or the brand's id, from the route. */
  id: string;
};

/**
 * The AI's suggestion for a strategy: one job started with `start(note)`,
 * then polled every 5s until it is done or failed.
 *
 * Lives on the strategy page, not in the modal, so closing the modal leaves
 * the job running and the page's button can say when it is ready. Leaving
 * the page unmounts it, which stops the polling.
 *
 * Starting is a mutation on purpose: every run is minutes of paid AI work and
 * must only ever follow a click. The backend hands back the running job when
 * one is already going for this location or brand, so a double click or a
 * second admin costs nothing extra.
 */
export const useStrategySuggestion = ({ scope, id }: Params) => {
  const queryClient = useQueryClient();
  const key = storageKey(scope, id);

  // Keyed by the route so moving to another location or brand without the
  // page remounting does not show the previous one's job.
  const [stored, setStored] = useState(() => ({
    key,
    jobId: readStoredJob(key),
  }));
  const jobId = stored.key === key ? stored.jobId : readStoredJob(key);

  const setJobId = (next: string | null) => {
    storeJob(key, next);
    setStored({ key, jobId: next });
  };

  const startMutation = useMutation({
    mutationFn: async (note: string) => {
      const trimmed = note.trim();
      const { data } = await startVDriveStrategySuggestion(scope, id, {
        note: trimmed || null,
      });
      return data.data;
    },
    onSuccess: (job) => {
      // Seeds the poll, so a job that comes back already finished (the
      // backend handing over one that was running) shows at once.
      queryClient.setQueryData(
        queryKeys.admin.vDriveStrategySuggestion(scope, id, job.jobId),
        job
      );
      setJobId(job.jobId);
    },
  });

  const jobQuery = useQuery({
    queryKey: queryKeys.admin.vDriveStrategySuggestion(scope, id, jobId ?? ''),
    queryFn: async (): Promise<VDriveStrategySuggestionJob> => {
      const { data } = await getVDriveStrategySuggestion(scope, id, jobId!);
      return data.data;
    },
    enabled: !!jobId,
    // A finished job never changes; only a pending one is asked again.
    staleTime: Infinity,
    retry: false,
    refetchOnWindowFocus: false,
    // The wait is minutes long and an admin will switch tabs meanwhile; keep
    // asking so the answer is there on return (the browser slows hidden
    // tabs' timers anyway, and it stops for good once the job is finished).
    refetchIntervalInBackground: true,
    refetchInterval: (query) => {
      if (isNotFound(query.state.error)) return false;
      const job = query.state.data;
      // A dropped poll keeps going: the job is still running server side.
      return !job || job.status === 'pending' ? POLL_MS : false;
    },
  });

  // An hour after it started the job is gone: forget it, the next click
  // starts a fresh one.
  const expired = isNotFound(jobQuery.error);
  const job = expired ? undefined : jobQuery.data;

  let status: StrategySuggestionStatus = 'idle';
  if (startMutation.isPending) status = 'starting';
  else if (startMutation.isError) status = 'failed';
  else if (job) status = job.status;
  else if (jobId && !expired) status = 'pending';

  const errorMessage = startMutation.isError
    ? startErrorMessage(startMutation.error)
    : job?.status === 'failed'
    ? job.error || 'The AI suggestion failed. Please try again.'
    : null;

  /** Back to the note form, for a new run. */
  const reset = () => {
    startMutation.reset();
    setJobId(null);
  };

  return {
    status,
    result: job?.status === 'done' ? job.result : null,
    errorMessage,
    start: (note: string) => startMutation.mutate(note),
    reset,
  };
};
